export type UIMode = 'beginner' | 'advanced';

const labels = {
  // Command Center / Dashboard
  equity: { beginner: 'Total Account Value', advanced: 'Equity' },
  openRisk: { beginner: 'Money at Risk', advanced: 'Open Risk' },
  drawdown: { beginner: 'Drop from Highest Value', advanced: 'Drawdown' },
  dailyPnL: { beginner: "Today's Gain/Loss", advanced: 'Daily PnL' },
  weeklyPnL: { beginner: "This Week's Gain/Loss", advanced: 'Weekly PnL' },
  winVsLoss: { beginner: 'Wins vs Losses (All Time)', advanced: 'Win / loss (all time)' },
  dailyLoss: { beginner: 'Daily Loss', advanced: 'Daily Loss' },
  weeklyLoss: { beginner: 'Weekly Loss', advanced: 'Weekly Loss' },
  correlationRisk: { beginner: 'Correlation Risk', advanced: 'Correlation Risk' },
  thingsToReview: { beginner: 'Things to Review', advanced: 'Alerts' },
  safetyCheck: { beginner: 'Safety Check', advanced: 'Protection Status' },
  status: { beginner: 'Status', advanced: 'Status' },
  activeGuardRails: { beginner: 'Safety Limits', advanced: 'Active Guard Rails' },
  protectionIntegrity: { beginner: 'Order Safety', advanced: 'Protective Order Integrity' },

  // Positions
  entryPrice: { beginner: 'Bought At', advanced: 'Entry' },
  currentPrice: { beginner: 'Current Price', advanced: 'Mark Price' },
  unrealizedPnL: { beginner: 'Gain/Loss', advanced: 'Unrealized PnL' },
  riskPercent: { beginner: 'Risk', advanced: 'Initial Risk %' },
  stopPrice: { beginner: 'Safety Stop', advanced: 'Stop' },
  takeProfitPrice: { beginner: 'Target Price', advanced: 'Take Profit' },
  safetyOrders: { beginner: 'Safety Orders', advanced: 'Stop State' },
  actionNeeded: { beginner: 'Action Needed', advanced: 'Risk Allocation' },

  // Events / Alerts
  alerts: { beginner: 'Things to Review', advanced: 'Alerts' },
  signalRejected: { beginner: 'Trade Skipped — Safety Rule Blocked It', advanced: 'Signal Rejected' },
  volatilityGuard: { beginner: 'Market Moved Too Fast — Waiting', advanced: 'Volatility Shock Guard Active' },
  syncFailure: { beginner: 'Stop Update Delayed', advanced: 'Protective Order Sync Failure' },

  // Review / Trades
  review: { beginner: 'Review', advanced: 'Review' },
  resultSummary: { beginner: 'Result Summary', advanced: 'Full Lifecycle Trace' },
  whyTaken: { beginner: 'Why Trade Was Taken', advanced: 'Decision Trace' },
  worstDip: { beginner: 'Worst Dip During Trade', advanced: 'MAE' },
  bestRun: { beginner: 'Best Run During Trade', advanced: 'MFE' },
  executionQuality: { beginner: 'Execution Quality', advanced: 'Slippage Metrics' },
  guardRailAtEntry: { beginner: 'Safety at Entry', advanced: 'Guard Rail State at Entry' },

  // Controls / Risk
  controls: { beginner: 'Controls', advanced: 'Controls' },
  pauseNewTrades: { beginner: 'Pause New Trades', advanced: 'Pause Entries' },
  safetyMode: { beginner: 'Switch to Safety Mode', advanced: 'Enter Preservation Mode' },
  closeAll: { beginner: 'Close All Trades Now', advanced: 'Flatten All Positions' },
  stopAutomated: { beginner: 'Stop Automated Trading', advanced: 'Disable Strategy' },
  resumeStrategy: { beginner: 'Resume Trading', advanced: 'Resume Strategy' },
  resume: { beginner: 'Resume', advanced: 'Resume' },
  enablePreservation: { beginner: 'Enable Safety Mode', advanced: 'Enable Preservation' },
  pauseStrategy: { beginner: 'Pause New Trades', advanced: 'Pause Strategy' },
  killAll: { beginner: 'Close All Trades Now', advanced: 'Kill All' },

  // Navigation
  commandCenter: { beginner: 'Command Center', advanced: 'Command Center' },
  positions: { beginner: 'Open Positions', advanced: 'Open Positions' },
  events: { beginner: 'Events', advanced: 'Events' },
  reviewNav: { beginner: 'Review', advanced: 'Review' },
  controlsNav: { beginner: 'Controls', advanced: 'Controls' },
  mt5Status: { beginner: 'MT5 Status', advanced: 'MT5 Status' },
  settings: { beginner: 'Settings', advanced: 'Settings' },
  signals: { beginner: 'Signals', advanced: 'Signals' },
  tradingViewSignals: { beginner: 'From TradingView', advanced: 'TradingView Signals' },
  audit: { beginner: 'Audit', advanced: 'Audit' },

  // Page titles
  dashboardTitle: { beginner: 'Command Center', advanced: 'Command Center' },
  dashboardDesc: { beginner: 'Your system safety at a glance', advanced: 'Breakout Engine Overview' },
  positionsTitle: { beginner: 'Open Positions', advanced: 'Open Positions' },
  positionsDesc: { beginner: 'Your open trades', advanced: 'Currently active trading positions' },
  alertsTitle: { beginner: 'Things to Review', advanced: 'Alerts' },
  alertsDesc: { beginner: 'Items that may need your attention', advanced: 'System alerts and events' },
  tradesTitle: { beginner: 'Trade History', advanced: 'Trades' },
  tradesDesc: { beginner: 'Past trades and their results', advanced: 'Closed trade history' },
  riskTitle: { beginner: 'Controls', advanced: 'Risk Monitor' },
  riskDesc: { beginner: 'System controls and safety', advanced: 'System risk status and controls' },
  settingsTitle: { beginner: 'Settings', advanced: 'Settings' },
  settingsDesc: { beginner: 'Your preferences', advanced: 'System configuration' },
  settingsHistoryTitle: { beginner: 'Settings History', advanced: 'Settings History' },
  settingsHistoryDesc: {
    beginner: 'View and reapply previous settings snapshots',
    advanced: 'Settings snapshots with P/L tracking per configuration',
  },
  settingsHistoryNav: { beginner: 'History', advanced: 'History' },
  signalsTitle: { beginner: 'From TradingView', advanced: 'TradingView Signal Log' },
  signalsDesc: { beginner: 'Incoming signals and their status', advanced: 'Signal log with decision trace' },
  howItWorks: { beginner: 'How It Works', advanced: 'How It Works' },
  howItWorksTitle: { beginner: 'How It Works', advanced: 'How It Works' },
  howItWorksDesc: { beginner: 'How the system works and what protects your capital', advanced: 'System flow and capital protection rules' },

  // Mode labels
  modeBeginner: { beginner: 'Beginner', advanced: 'Beginner' },
  modeAdvanced: { beginner: 'Advanced', advanced: 'Advanced' },

  // Alert severity (display)
  severityInfo: { beginner: 'Info', advanced: 'INFO' },
  severityWarn: { beginner: 'Monitor', advanced: 'WARN' },
  severityHigh: { beginner: 'Attention', advanced: 'HIGH' },
  severityCritical: { beginner: 'Critical', advanced: 'CRITICAL' },
} as const;

export type TerminologyKey = keyof typeof labels;

export function getLabel(key: TerminologyKey, mode: UIMode): string {
  const entry = labels[key];
  return entry ? entry[mode] : key;
}

export function getSeverityLabel(severity: string, mode: UIMode): string {
  const map: Record<string, TerminologyKey> = {
    INFO: 'severityInfo',
    WARN: 'severityWarn',
    HIGH: 'severityHigh',
    CRITICAL: 'severityCritical',
  };
  const key = map[severity] ?? 'severityInfo';
  return getLabel(key, mode);
}
